import { ColDef, ColGroupDef, ColumnMenuTab } from "ag-grid-enterprise";
import {
  numberCellEditorParams,
  numberFilterParams
} from "components/cell-editor/numberCellEditor";
import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import { DateFilter, DateUtils } from "utils/dateutils";

export const getMilestoneBySequenceNo = (params: any, sequenceNo: number) => {
  const milestones = params?.data?.date_block?.milestones;
  if (!Array.isArray(milestones) || milestones.length === 0) {
    return undefined;
  }

  return milestones.find(
    (milestone: any) => milestone.sequence_no === sequenceNo
  );
};

/** Standalone date-block summary columns — not per-milestone leaf columns. */
export const PRESERVE_MILESTONE_NAMED_COLUMN_IDS = new Set([
  "planned_next_milestone_date",
  "projected_next_milestone_date",
  "projected_last_milestone_date"
]);

export function isMilestoneOrDurationColumnId(name: string) {
  if (!name || PRESERVE_MILESTONE_NAMED_COLUMN_IDS.has(name)) {
    return false;
  }

  return (
    name.startsWith("milestone_") ||
    name.startsWith("offset_") ||
    name.includes("milestone_") ||
    name.includes("offset_")
  );
}

export function getInterleavedDateBlockColumnIds(
  columnDefsForDateBlock: ColDef[]
): string[] {
  return columnDefsForDateBlock
    .map((col) => col.colId)
    .filter((id): id is string => Boolean(id));
}

export function generateSavedColConfigsForColIds(
  colIds: string[],
  order: number,
  {
    milestonesColumnVisibility,
    durationsColumnVisibility
  }: {
    milestonesColumnVisibility: boolean;
    durationsColumnVisibility: boolean;
  },
  existingLeaf: Array<{
    field: { name: string; [key: string]: unknown };
    order?: number;
    visibility?: boolean;
  }> = []
) {
  const configStructure: any = {
    field: {
      name: "",
      range: null,
      filter: null,
      agFilter: null,
      sortOrder: null
    },
    order: null,
    visibility: true
  };

  const existingByName = new Map(
    existingLeaf.map((col) => [col.field.name, col])
  );

  return colIds.map((colId, index) => {
    const existing = existingByName.get(colId);
    const isMilestoneCol = colId.includes("milestone_");

    if (existing) {
      return {
        ...existing,
        field: {
          ...configStructure.field,
          ...existing.field,
          name: colId
        },
        order: order + index + 1,
        visibility:
          existing.visibility ??
          (isMilestoneCol
            ? milestonesColumnVisibility
            : durationsColumnVisibility)
      };
    }

    return {
      field: {
        ...configStructure.field,
        name: colId
      },
      order: order + index + 1,
      visibility: isMilestoneCol
        ? milestonesColumnVisibility
        : durationsColumnVisibility
    };
  });
}

export function getMaxNumberOfMilestonesFromEntityList(
  entityList: any[] | null
) {
  if (!entityList) return null;
  const hasOffsetByIndex = new Set<number>();

  const milestoneHasValue = (m: any) => {
    return (
      (m?.name != null && String(m.name).trim() !== "") ||
      m?.planned != null ||
      m?.projected != null ||
      m?.actual != null
    );
  };

  const offsetHasValue = (m: any) => {
    return (
      (m?.offset_name != null && String(m.offset_name).trim() !== "") ||
      m?.offset_duration != null
    );
  };

  const milestonesCount = entityList.reduce((max: number, item: any) => {
    const milestones = item?.date_block?.milestones || [];
    if (!Array.isArray(milestones) || milestones.length === 0) {
      return max;
    }

    let effectiveMaxSeq = 0;
    milestones.forEach((m: any, idx: number) => {
      const seq = typeof m?.sequence_no === "number" ? m.sequence_no : idx + 1;

      if (milestoneHasValue(m) || offsetHasValue(m)) {
        effectiveMaxSeq = Math.max(effectiveMaxSeq, seq);
      }

      if (offsetHasValue(m)) {
        hasOffsetByIndex.add(seq);
      }
    });

    return Math.max(max, effectiveMaxSeq);
  }, 0);

  const lastMilestoneHasOffset =
    milestonesCount > 0 ? hasOffsetByIndex.has(milestonesCount) : false;

  return {
    milestonesCount,
    lastMilestoneHasOffset,
    milestoneIndicesWithOffset: hasOffsetByIndex
  };
}

function getOffsetColumnDefs(
  milestoneArrayIndex: number,
  dbColsDefaultProperties: any
) {
  return [
    {
      colId: `name_offset_${milestoneArrayIndex}`,
      headerName: `Duration Name ${milestoneArrayIndex}`,
      headerTooltip: `Duration Name ${milestoneArrayIndex}`,
      ...dbColsDefaultProperties,
      valueGetter: (params: any) => {
        const milestoneBySequenceNo = getMilestoneBySequenceNo(
          params,
          milestoneArrayIndex
        );
        return milestoneBySequenceNo?.offset_name;
      }
    },
    {
      colId: `duration_offset_${milestoneArrayIndex}`,
      headerName: `Duration ${milestoneArrayIndex}`,
      headerTooltip: `Duration ${milestoneArrayIndex}`,
      ...dbColsDefaultProperties,
      valueGetter: (params: any) => {
        const milestoneBySequenceNo = getMilestoneBySequenceNo(
          params,
          milestoneArrayIndex
        );
        return milestoneBySequenceNo?.offset_duration;
      },
      filter: "agNumberColumnFilter",
      filterParams: numberFilterParams,
      editable: true,
      cellEditor: "agNumberCellEditor",
      cellEditorParams: numberCellEditorParams,
      cellClass: (params: any) => {
        const { context } = params;
        if (!context?.canInlineEditDBOffset) return "";
        const milestoneBySequenceNo = getMilestoneBySequenceNo(
          params,
          milestoneArrayIndex
        );
        return milestoneBySequenceNo?.is_editable ? "cell-editable" : "";
      }
    }
  ];
}

export function getSidebarColumnLayout(
  maxNumberOfMilestones: number | null,
  milestoneIndicesWithOffset?: Set<number>
) {
  const nonDraggableToolPanelClass = "md-tree-non-draggable";
  const milestonesGroupCol: any = {
    headerName: "Milestones",
    groupId: "milestones_group",
    openByDefault: false,
    toolPanelClass: nonDraggableToolPanelClass,
    children: []
  };
  const durationsGroupCol: any = {
    headerName: "Durations",
    groupId: "durations_group",
    openByDefault: false,
    toolPanelClass: nonDraggableToolPanelClass,
    children: []
  };
  const sidebarColumnLayout: (ColDef | ColGroupDef)[] = [
    milestonesGroupCol,
    durationsGroupCol
  ];

  const milestonesToRender =
    maxNumberOfMilestones != null && maxNumberOfMilestones > 0
      ? maxNumberOfMilestones
      : 0;

  for (let i = 0; i < milestonesToRender; i += 1) {
    const milestoneArrayIndex = i + 1;
    const milestoneGroupCol = {
      headerName: `Milestone ${milestoneArrayIndex}`,
      groupId: `milestone_group_${milestoneArrayIndex}`,
      suppressMovable: true,
      openByDefault: false,
      toolPanelClass: nonDraggableToolPanelClass,
      children: [
        {
          colId: `name_milestone_${milestoneArrayIndex}`,
          headerName: `Milestone Name ${milestoneArrayIndex}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        },
        {
          colId: `planned_milestone_${milestoneArrayIndex}`,
          headerName: `Planned Date ${milestoneArrayIndex}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        },
        {
          colId: `projected_milestone_${milestoneArrayIndex}`,
          headerName: `Projected Date ${milestoneArrayIndex}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        },
        {
          colId: `actual_milestone_${milestoneArrayIndex}`,
          headerName: `Actual Date ${milestoneArrayIndex}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        }
      ]
    };
    const durationGroupCol = {
      headerName: `Duration ${milestoneArrayIndex}`,
      groupId: `duration_group_${milestoneArrayIndex}`,
      suppressMovable: true,
      openByDefault: false,
      toolPanelClass: nonDraggableToolPanelClass,
      children: [
        {
          colId: `name_offset_${milestoneArrayIndex}`,
          headerName: `Duration Name ${milestoneArrayIndex}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        },
        {
          colId: `duration_offset_${milestoneArrayIndex}`,
          headerName: `Duration ${milestoneArrayIndex}`,
          suppressMovable: true,
          toolPanelClass: nonDraggableToolPanelClass
        }
      ]
    };

    milestonesGroupCol.children.push(milestoneGroupCol);

    const hasOffset =
      !milestoneIndicesWithOffset ||
      milestoneIndicesWithOffset.has(milestoneArrayIndex);
    if (hasOffset) {
      durationsGroupCol.children.push(durationGroupCol);
    }
  }

  return {
    sidebarColumnLayout
  };
}

function featureDateBlockColumns(
  maxNumberOfMilestones: number | null,
  addLastOffsetColumns: boolean,
  milestoneIndicesWithOffset?: Set<number>
) {
  const groupCols: ColDef[] = [
    {
      colId: "_milestones",
      headerName: "Milestones",
      width: 0,
      maxWidth: 0,
      headerClass: "!p-0 !w-0",
      filter: false,
      editable: false,
      suppressMovable: true,
      getQuickFilterText: () => "",
      // hide: true,
      lockPosition: "right"
    },
    {
      colId: "_durations",
      headerName: "Durations",
      width: 0,
      maxWidth: 0,
      headerClass: "!p-0 !w-0",
      filter: false,
      editable: false,
      suppressMovable: true,
      getQuickFilterText: () => "",
      // hide: true,
      lockPosition: "right"
    }
  ];

  const dbColsDefaultProperties = {
    // suppressColumnsToolPanel: true,
    suppressMovable: true,
    menuTabs: ["filterMenuTab"] as ColumnMenuTab[],
    editable: false,
    width: 180,
    lockPosition: "right" as const
  };

  const columnDefsForDateBlock: ColDef[] = [];

  const count =
    maxNumberOfMilestones != null && maxNumberOfMilestones > 0
      ? maxNumberOfMilestones
      : 0;
  for (let i = 0; i < count; i += 1) {
    const milestoneArrayIndex = i + 1;
    columnDefsForDateBlock.push(
      {
        colId: `name_milestone_${milestoneArrayIndex}`,
        headerName: `Milestone Name ${milestoneArrayIndex}`,
        headerTooltip: `Milestone Name ${milestoneArrayIndex}`,
        ...dbColsDefaultProperties,
        valueGetter: (params: any) => {
          const milestoneBySequenceNo = getMilestoneBySequenceNo(
            params,
            milestoneArrayIndex
          );
          return milestoneBySequenceNo?.name;
        }
      },
      {
        colId: `planned_milestone_${milestoneArrayIndex}`,
        headerName: `Planned Date ${milestoneArrayIndex}`,
        headerTooltip: `Planned Date ${milestoneArrayIndex}`,
        ...dbColsDefaultProperties,
        valueGetter: (params: any) => {
          const milestoneBySequenceNo = getMilestoneBySequenceNo(
            params,
            milestoneArrayIndex
          );
          return milestoneBySequenceNo?.planned
            ? DateUtils.format(milestoneBySequenceNo?.planned)
            : undefined;
        },
        filter: CustomDateSetFilter,
        comparator: DateFilter.comparator
      },
      {
        colId: `projected_milestone_${milestoneArrayIndex}`,
        headerName: `Projected Date ${milestoneArrayIndex}`,
        headerTooltip: `Projected Date ${milestoneArrayIndex}`,
        ...dbColsDefaultProperties,
        valueGetter: (params: any) => {
          const milestoneBySequenceNo = getMilestoneBySequenceNo(
            params,
            milestoneArrayIndex
          );
          return milestoneBySequenceNo?.projected
            ? DateUtils.format(milestoneBySequenceNo?.projected)
            : undefined;
        },
        filter: CustomDateSetFilter,
        comparator: DateFilter.comparator
      },
      {
        colId: `actual_milestone_${milestoneArrayIndex}`,
        headerName: `Actual Date ${milestoneArrayIndex}`,
        headerTooltip: `Actual Date ${milestoneArrayIndex}`,
        ...dbColsDefaultProperties,
        valueGetter: (params: any) => {
          const milestoneBySequenceNo = getMilestoneBySequenceNo(
            params,
            milestoneArrayIndex
          );
          return milestoneBySequenceNo?.actual
            ? DateUtils.format(milestoneBySequenceNo?.actual)
            : undefined;
        },
        filter: CustomDateSetFilter,
        comparator: DateFilter.comparator
      }
    );

    const isLast = i === count - 1;
    const hasOffset =
      !milestoneIndicesWithOffset ||
      milestoneIndicesWithOffset.has(milestoneArrayIndex);

    if (hasOffset && (!isLast || addLastOffsetColumns)) {
      columnDefsForDateBlock.push(
        ...getOffsetColumnDefs(milestoneArrayIndex, dbColsDefaultProperties)
      );
    }
  }

  const dateBlockColumnIds = {
    milestones: columnDefsForDateBlock
      .map((col) => col.colId)
      .filter((col) => col?.includes("milestone_")),
    durations: columnDefsForDateBlock
      .map((col) => col.colId)
      .filter((col) => col?.includes("offset_"))
  };

  return {
    groupCols,
    columnDefsForDateBlock,
    dateBlockColumnIds
  };
}

export default featureDateBlockColumns;
